import React, { createContext, useContext, useState, useEffect } from 'react';
import { DEFAULT_SITE_CONFIG, SiteConfig } from '../data/defaultConfig';
import { loadConfigFromSupabase, saveConfigToSupabase } from '../services/supabaseService';
import { isSupabaseConfigured } from '../lib/supabase';

interface SiteConfigContextType {
  config: SiteConfig;
  loading: boolean;
  updateConfig: (newConfig: Partial<SiteConfig>) => Promise<void>;
  resetConfig: () => void;
}

const SiteConfigContext = createContext<SiteConfigContextType | undefined>(undefined);

export const SiteConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<SiteConfig>(DEFAULT_SITE_CONFIG);
  const [loading, setLoading] = useState(true);

  const mergeConfigs = (base: SiteConfig, target: Partial<SiteConfig>): SiteConfig => {
    return {
      phoneNumberTony: target.phoneNumberTony || base.phoneNumberTony,
      phoneNumberNila: target.phoneNumberNila || base.phoneNumberNila,
      whatsappMessageTonyEn: target.whatsappMessageTonyEn || base.whatsappMessageTonyEn,
      whatsappMessageTonyId: target.whatsappMessageTonyId || base.whatsappMessageTonyId,
      whatsappMessageNilaEn: target.whatsappMessageNilaEn || base.whatsappMessageNilaEn,
      whatsappMessageNilaId: target.whatsappMessageNilaId || base.whatsappMessageNilaId,
      googleRating: typeof target.googleRating === 'number' ? target.googleRating : base.googleRating,
      googleReviewCount: typeof target.googleReviewCount === 'number' ? target.googleReviewCount : base.googleReviewCount,
      slides: target.slides && target.slides.length > 0 ? target.slides : base.slides,
      galleryItems: target.galleryItems && target.galleryItems.length > 0 ? target.galleryItems : base.galleryItems,
      reviews: target.reviews && target.reviews.length > 0 ? target.reviews : base.reviews,
    };
  };

  useEffect(() => {
    const loadConfig = async () => {
      try {
        // PRIORITY 1: Load from Supabase (live database — preferred)
        if (isSupabaseConfigured) {
          const supabaseConfig = await loadConfigFromSupabase();
          if (supabaseConfig) {
            setConfig(mergeConfigs(DEFAULT_SITE_CONFIG, supabaseConfig));
            setLoading(false);
            return;
          }
        }

        // PRIORITY 2: localStorage (local admin preview edits, no Supabase yet)
        const localData = localStorage.getItem('tncakes_site_config');
        if (localData) {
          try {
            const parsed = JSON.parse(localData);
            setConfig(mergeConfigs(DEFAULT_SITE_CONFIG, parsed));
            setLoading(false);
            return;
          } catch (e) {
            console.error('Error parsing site config from localStorage:', e);
          }
        }

        // PRIORITY 3: Fetch from public/siteConfig.json (static export fallback)
        const response = await fetch('/siteConfig.json');
        if (response.ok) {
          const fetchedData = await response.json();
          setConfig(mergeConfigs(DEFAULT_SITE_CONFIG, fetchedData));
        } else {
          setConfig(DEFAULT_SITE_CONFIG);
        }
      } catch (error) {
        console.error('Failed to load site configuration:', error);
        setConfig(DEFAULT_SITE_CONFIG);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  /**
   * Update site configuration.
   * - Saves to Supabase (live, instant for all visitors) if configured.
   * - Also saves to localStorage as a quick preview/fallback.
   */
  const updateConfig = async (newConfig: Partial<SiteConfig>) => {
    const merged = mergeConfigs(config, newConfig);
    setConfig(merged);

    // Save to localStorage always (instant preview fallback)
    localStorage.setItem('tncakes_site_config', JSON.stringify(merged));

    // Save to Supabase if configured (makes changes live for everyone)
    if (isSupabaseConfigured) {
      const success = await saveConfigToSupabase(merged);
      if (!success) {
        console.error('Supabase save failed — changes saved to localStorage only.');
        throw new Error('Supabase database save failed.');
      }
    }
  };

  const resetConfig = () => {
    localStorage.removeItem('tncakes_site_config');
    setConfig(DEFAULT_SITE_CONFIG);
  };

  return (
    <SiteConfigContext.Provider value={{ config, loading, updateConfig, resetConfig }}>
      {children}
    </SiteConfigContext.Provider>
  );
};

export const useSiteConfig = () => {
  const context = useContext(SiteConfigContext);
  if (context === undefined) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
};
